import { FC, useContext, useRef, useState } from "react";
import CN from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import AddProductContext from "./memory/AddProductContext";
import { Button } from "../../components/Button";
import { FaPlus } from "react-icons/fa";
import ImageUpload from "./plugins/ImageUpload";
import { useSelector } from "react-redux";
import Select, { ActionMeta, SingleValue } from "react-select";
import CreatableSelect from "react-select/creatable";
import store from "../../store/store";
import { addProduct } from "../Products/reducer/productSlice";
import { Oval } from "react-loader-spinner";

interface FormValues {
  name: string;
  description: string;
  walk_in_customer_price: number;
  customer_price: number;
  stockQuantity: number;
  // recorderLevel: number;
  category: string;
  sku: string;
  brand: string;
  isFeatured: boolean;
  isPopular: boolean;
  status: boolean;
  images?: File[] | string;
  tags: any;
  userTags: any;
  offer: any;
}

interface ProductImage {
  path: string;
  public_id: string;
  primaryImg: boolean;
  _id: string;
}

export const AddProduct: React.FC<any> = () => {
  const addProductContext = useContext(AddProductContext);
  const { categoryData, brandsData, tagsData, isLoading, addProductRes } =
    useSelector((state: any) => state.rootReducer?.product);

  const { fullResponse } = useSelector(
    (state: any) => state.rootReducer.offers
  );

  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Product Name is required")
      .max(50, "Product Name must be 50 characters or less"),
    sku: Yup.string().required("Product SKU is required"),
    description: Yup.string().required("Description is required"),
    walk_in_customer_price: Yup.number()
      .required("Retail Price is required")
      .min(0, "Retail Price must be greater than or equal to 0"),
    customer_price: Yup.number()
      .required("Wholesaler Price is required")
      .min(0, "Wholesaler Price must be greater than or equal to 0"),
    category: Yup.string().required("Category is required"),
    brand: Yup.string().required("Brand is required"),
    offer: Yup.string(),
    stockQuantity: Yup.string()
      .required("Quantity is required")
      .min(0, "Quantity must be greater than or equal to 0"),
    images: Yup.array().min(1, "At least one image is required"),
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    errors,
    touched,
  } = useFormik<FormValues>({
    initialValues: {
      name: "",
      description: "",
      walk_in_customer_price: 0,
      customer_price: 0,
      stockQuantity: 0,
      // recorderLevel: 0,
      category: "",
      brand: "",
      sku: "Product-" + new Date().valueOf(),
      images: [],
      tags: [],
      offer: "",
      userTags: [],
      isFeatured: false,
      isPopular: false,
      status: true,
    },
    validationSchema,
    onSubmit: (values: FormValues) => {
      const formData = new FormData();
      console.log("values", values);

      Object.entries(values).forEach(([key, value]) => {
        if (key === "images") {
          // Append images with a custom key
          (value as File[]).forEach((file) => {
            formData.append("images", file);
          });
        } else if (Array.isArray(value)) {
          value.forEach((val) => formData.append(`${key}[]`, val));
        } else {
          formData.append(key, value as string);
        }
      });

      console.log("formData", formData);

      // addProductContext?.addNewProduct(formData);
      store.dispatch(addProduct(formData));
    },
  });

  const transformedCategoryData =
    categoryData &&
    categoryData.map((category: { slug: any; category: any; _id: any }) => ({
      value: category.slug,
      label: category.category,
      id: category._id,
    }));

  const transformedBrandsData =
    brandsData &&
    brandsData.map((brand: { slug: any; name: any; _id: any }) => ({
      value: brand.slug,
      label: brand.name,
      id: brand._id,
    }));

    const transformedOffersData =
    fullResponse &&
    fullResponse.map((offer: {discountPrecentage: any; name: any; _id: any }) => ({
      label: offer.discountPrecentage,
      id: offer._id,
      discountPrecentage: offer.discountPrecentage,
    }));

  const transformedTagData =
    tagsData &&
    tagsData.map((tag: { name: any; _id: any }) => ({
      label: tag.name,
      id: tag._id,
    }));

  const handleImageUpload = (images: (File | ProductImage)[]) => {
    // setUploadedFiles(files);
    setFieldValue("images", images);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <form onSubmit={handleSubmit}>
        <div className="lg:grid grid-cols-2 flex flex-col gap-6 my-4 py-8 lg:mx-8 mx-4">
          {/* Product Images */}
          <div className="col-span-2">
            <label className="block text-base font-semibold mb-2">
              Product Images <span className="text-red-500">*</span>
            </label>

            <ImageUpload onUpload={handleImageUpload} />

            {errors.images && touched.images && (
              <p className="text-red-500 text-sm my-2">{errors.images}</p>
            )}
          </div>

          {/* Product Name */}
          <div className="col-span-1">
            <label className="block text-base font-semibold mb-2">
              Name of the product <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Untitled product"
              className={CN(
                "block w-full p-3 border rounded-md",
                errors.name && touched.name
                  ? "border-red-500"
                  : "border-primary1"
              )}
            />
            {errors.name && touched.name && (
              <p className="text-red-500 text-sm my-2">{errors.name}</p>
            )}
          </div>

          <div className="col-span-1">
            <label className="block text-base font-semibold mb-2">
              Product Code/SKU <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="sku"
              value={values.sku}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Untitled product"
              className={CN(
                "block w-full p-3 border rounded-md",
                errors.sku && touched.sku ? "border-red-500" : "border-primary1"
              )}
            />
            {errors.sku && touched.sku && (
              <p className="text-red-500 text-sm my-2">{errors.sku}</p>
            )}
          </div>

          {/* Description */}
          <div className="col-span-1">
            <div className="flex flex-col gap-1 mb-2">
              <label className="block text-base font-semibold">
                Description
                <span className="text-red-500">*</span>
              </label>
              <span className="font-normal text-sm">
                Describe the features and information of your product with
                details for your customers
              </span>
            </div>
            <textarea
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              className={CN(
                "block w-full p-3 border rounded-md",
                errors.description && touched.description
                  ? "border-red-500"
                  : "border-primary1"
              )}
            />
            {errors.description && touched.description && (
              <p className="text-red-500 text-sm my-2">{errors.description}</p>
            )}
          </div>

          {/* Tags */}
          <div className="col-span-1">
            <div className="flex flex-col gap-1 mb-2">
              <label className="block text-base font-semibold">Tags</label>
              <span className="font-normal text-sm">Add up to 5 tags</span>
            </div>

            <CreatableSelect
              isClearable
              className="cs-select"
              classNamePrefix="cs-select"
              isOptionDisabled={() =>
                values.tags.length + (values.userTags?.length || 0) >= 5
              }
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.id}
              getNewOptionData={(inputValue) => ({
                id: inputValue, // Use the input value directly as the id
                label: inputValue, // Use the input value as the label
                __isNew__: true,
              })}
              onChange={(newValue) => {
                // Separate new user-created tags from existing tags
                const existingTags: any = [];
                const newTags: any = [];

                (newValue || []).forEach((tag) => {
                  if (tag.__isNew__) {
                    newTags.push(tag.label); // Add the label of new tags (without #)
                  } else {
                    existingTags.push(tag.id); // Add the id of existing tags
                  }
                });

                // Update form values
                setFieldValue("tags", existingTags); // Pass existing tag IDs to the API
                setFieldValue("userTags", newTags); // Pass new user tags to the API
              }}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: "#29314f",
                  borderWidth: 1,
                  borderRadius: 6,
                }),
              }}
              isMulti={true}
              options={transformedTagData}
              value={[
                ...transformedTagData.filter((tag: any) =>
                  values.tags.includes(tag.id)
                ), // Existing tags
                ...(values.userTags || []).map((tag: any) => ({
                  id: tag,
                  label: tag,
                  __isNew__: true,
                })), // New user-created tags
              ]}
              placeholder="Add tags (e.g., electronics)"
            />

            {/* {errors.tags && touched.tags && (
        <p className="text-red-500 text-sm">{errors.tags}</p>
      )} */}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Retail Price */}
            <div>
              <label className="block text-base font-semibold mb-2">
                Retail Price (AED) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="walk_in_customer_price"
                value={values.walk_in_customer_price}
                onChange={handleChange}
                onBlur={handleBlur}
                className={CN(
                  "block w-full p-3 border rounded-md",
                  errors.walk_in_customer_price &&
                    touched.walk_in_customer_price
                    ? "border-red-500"
                    : "border-primary1"
                )}
              />
              {errors.walk_in_customer_price &&
                touched.walk_in_customer_price && (
                  <p className="text-red-500 text-sm my-2">
                    {errors.walk_in_customer_price}
                  </p>
                )}
            </div>

            {/* Wholesale Price */}
            <div>
              <label className="block text-base font-semibold mb-2">
                Wholesaler Price (AED) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="customer_price"
                value={values.customer_price}
                onChange={handleChange}
                onBlur={handleBlur}
                className={CN(
                  "block w-full p-3 border rounded-md",
                  errors.customer_price && touched.customer_price
                    ? "border-red-500"
                    : "border-primary1"
                )}
              />
              {errors.customer_price && touched.customer_price && (
                <p className="text-red-500 text-sm my-2">
                  {errors.customer_price}
                </p>
              )}
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-base font-semibold mb-2">
                Available Quantity <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="stockQuantity"
                value={values.stockQuantity}
                onChange={handleChange}
                onBlur={handleBlur}
                className={CN(
                  "block w-full p-3 border rounded-md",
                  errors.stockQuantity && touched.stockQuantity
                    ? "border-red-500"
                    : "border-primary1"
                )}
              />
              {errors.stockQuantity && touched.stockQuantity && (
                <p className="text-red-500 text-sm my-2">
                  {errors.stockQuantity}
                </p>
              )}
            </div>
          </div>

          <div className="col-span-1 grid gap-4">
            <div className="md:grid grid-cols-2 flex flex-col gap-4">
              {/* Category */}
              <div>
                <label className="block text-base font-semibold mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <Select
                  placeholder="Select a category"
                  options={transformedCategoryData}
                  onChange={(
                    option: SingleValue<{ id: string; label: string }>
                  ) => {
                    setFieldValue("category", option?.id);
                  }}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderColor: "#29314f",
                      borderWidth: 1,
                      borderRadius: 6,
                    }),
                  }}
                />
                {errors.category && touched.category && (
                  <p className="text-red-500 text-sm my-2">{errors.category}</p>
                )}
              </div>

              {/* Brand */}
              <div>
                <label className="block text-base font-semibold mb-2">
                  Brand <span className="text-red-500">*</span>
                </label>
                <Select
                  placeholder="Select a brand"
                  options={transformedBrandsData}
                  onChange={(
                    option: SingleValue<{ id: string; label: string }>
                  ) => {
                    setFieldValue("brand", option?.id);
                  }}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderColor: "#29314f",
                      borderWidth: 1,
                      borderRadius: 6,
                    }),
                  }}
                />
                {errors.brand && touched.brand && (
                  <p className="text-red-500 text-sm my-2">{errors.brand}</p>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-1">
            <label className="block text-base font-semibold mb-2">
              Offer(%)
            </label>
            <Select
              placeholder="Select a Discount"
              options={transformedOffersData}
              onChange={(
                option: SingleValue<{ id: string; label: string }>
              ) => {
                setFieldValue("offer", option?.id);
              }}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: "#29314f",
                  borderWidth: 1,
                  borderRadius: 6,
                }),
              }}
            />
            {/* {errors.offer && touched.offer && (
              <p className="text-red-500 text-sm my-2">{errors.offer}</p>
            )} */}
          </div>

          {/* Featured Product */}
          <div className="col-span-2 flex items-start">
            <input
              type="checkbox"
              name="isFeatured"
              className="accent-primary1 w-[18px] h-[18px] mr-3 my-1 cursor-pointer rounded border border-primary1"
              onChange={(e) => setFieldValue("isFeatured", e.target.checked)}
              checked={values.isFeatured}
            />
            <div className="flex flex-col gap-1 mb-2">
              <label className="block text-base font-semibold">
                Featured Product
              </label>
              <span className="font-normal text-sm">
                Mark this product as featured
              </span>
            </div>
          </div>

          {/* Popular Product */}
          <div className="col-span-2 flex items-start">
            <input
              type="checkbox"
              name="isPopular"
              className="accent-primary1 w-[18px] h-[18px] mr-3 my-1 cursor-pointer rounded border border-primary1"
              onChange={(e) => setFieldValue("isPopular", e.target.checked)}
              checked={values.isPopular}
            />
            <div className="flex flex-col gap-1 mb-2">
              <label className="block text-base font-semibold">
                Popular Product
              </label>
              <span className="font-normal text-sm">
                Mark this product as popular
              </span>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div
          className={`flex justify-end flex-row gap-4 mt-5 py-4 w-full sticky bottom-0 bg-primary2 shadow-tableShadow lg:px-8 px-4`}
        >
          <div className="col-span-2 flex justify-end gap-5">
            <Button onClick={() => navigate("/products")} apparence="gray">
              Cancel
            </Button>

            <Button
              isLoading={isLoading.productAdd}
              view="default"
              type="submit"
            >
              <div className="flex gap-1">
                {isLoading.productAdd && (
                  <Oval
                    height={18}
                    width={18}
                    color="#ffff"
                    wrapperStyle={{}}
                    wrapperClass="max-sm:mt-[3.5px] mt-[5.2px] max-lg:mt-[5.2px]"
                    visible={true}
                    ariaLabel="oval-loading"
                    secondaryColor="gray-primary"
                    strokeWidth={6}
                    strokeWidthSecondary={6}
                  />
                )}
                <div className="font-normal text-base">Save</div>
              </div>
            </Button>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default AddProduct;
